import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { brl } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Reports() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [d, setD] = useState(null);
  const [costs, setCosts] = useState(null);

  const load = () => {
    api.get("/reports/summary", { params: { date_from: from, date_to: to } })
      .then((r) => setD(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/costs/pricing-context").then((r) => setCosts(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-5 animate-in-up" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Relatórios</h1>
          <p className="text-sm text-slate-500">Desempenho da sua operação</p>
        </div>
        <div className="flex gap-2 items-end">
          <div className="space-y-1">
            <Label className="text-xs">De</Label>
            <Input type="date" className="h-11" data-testid="report-from" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Até</Label>
            <Input type="date" className="h-11" data-testid="report-to" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button onClick={load} className="h-11 px-4 rounded-md bg-sky-600 text-white text-sm font-medium hover:bg-sky-700" data-testid="report-filter-btn">
            Filtrar
          </button>
        </div>
      </div>

      {!d ? (
        <p className="text-slate-400 text-sm">Carregando...</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ["Faturamento", brl(d.faturamento)],
              ["Lucro estimado", brl(d.lucro_estimado)],
              ["Ticket médio", brl(d.ticket_medio)],
              ["Margem média", `${d.margem_media}%`],
              ["Orçamentos", d.total_orcamentos],
              ["Aprovados", d.aprovados],
              ["Recusados", d.recusados],
              ["Contribuições à base", d.contribuicoes_mercado],
            ].map(([l, v]) => (
              <Card key={l} className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  <p className="text-xs font-mono-num uppercase tracking-widest text-slate-500">{l}</p>
                  <p className="mt-1 font-mono-num text-xl font-semibold">{v}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {costs?.configured && (
            <Card className="border-sky-200 bg-sky-50 shadow-sm" data-testid="report-costs-card">
              <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-sky-700">Seu custo/hora</p>
                  <p className="font-mono-num text-lg font-semibold text-slate-900" data-testid="report-custo-hora">{costs.custo_hora ? brl(costs.custo_hora) : "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-sky-700">Hora sugerida</p>
                  <p className="font-mono-num text-lg font-semibold text-slate-900" data-testid="report-hora-sugerida">{costs.valor_hora_sugerido ? brl(costs.valor_hora_sugerido) : "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-sky-700">Ponto de equilíbrio/mês</p>
                  <p className="font-mono-num text-lg font-semibold text-slate-900" data-testid="report-pe">{costs.ponto_equilibrio ? brl(costs.ponto_equilibrio) : "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-sky-700">Faturamento no período</p>
                  <p className="font-mono-num text-lg font-semibold text-slate-900" data-testid="report-fat-vs-pe">{brl(d.faturamento)}</p>
                  {costs.ponto_equilibrio > 0 && (
                    <p className={`text-[10px] ${d.faturamento >= costs.ponto_equilibrio ? "text-emerald-700" : "text-amber-700"}`} data-testid="report-pe-status">
                      {d.faturamento >= costs.ponto_equilibrio ? "acima do ponto de equilíbrio" : "abaixo do ponto de equilíbrio mensal"}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base">Faturamento por mês (aprovados)</CardTitle></CardHeader>
            <CardContent className="h-64">
              {d.por_mes.length === 0 ? (
                <p className="text-sm text-slate-500">Sem dados no período.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.por_mes}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="mes" fontSize={12} />
                    <YAxis fontSize={12} tickFormatter={(v) => `R$${(v / 1000).toFixed(1)}k`} />
                    <Tooltip formatter={(v) => brl(v)} />
                    <Bar dataKey="total" fill="#0284C7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base">Serviços por categoria</CardTitle></CardHeader>
            <CardContent>
              {d.por_servico.length === 0 ? (
                <p className="text-sm text-slate-500">Sem dados no período.</p>
              ) : (
                <div className="space-y-2">
                  {d.por_servico.map((s) => (
                    <div key={s.service} className="flex items-center justify-between border-b border-slate-100 pb-2 text-sm" data-testid={`report-service-${s.service.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <span>{s.service} <span className="text-slate-400">({s.count})</span></span>
                      <span className="font-mono-num font-semibold">{brl(s.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
